/**
 * Is the user typing into something? The menu screens are real DOM, so the
 * game must not swallow keys aimed at a control the browser owns.
 */
function isTextEntry(el) {
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
    || el.isContentEditable === true;
}

/**
 * Pointer-lock mouse look + keyboard state. Exposes edge-triggered helpers so
 * gameplay code can ask "was this pressed this frame" without tracking history.
 */
export class Input {
  /**
   * Keys the game acts on and the browser must not. Tab is the scoreboard and
   * would otherwise walk focus into the browser chrome; Space is jump and
   * would scroll or re-activate a focused button; the arrows scroll.
   */
  static GAME_KEYS = new Set([
    'Tab', 'Space',
    'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  ]);

  /**
   * Letter keys the browser owns a Ctrl/Cmd shortcut for.
   *
   * ## Why this is a gameplay bug and not a nicety
   *
   * Crouch is `ControlLeft` and the movement keys are WASD, so every
   * crouch-walk is a browser shortcut:
   *
   *   Ctrl+S  crouch + back      -> Save page dialog
   *   Ctrl+D  crouch + right     -> Bookmark this tab
   *   Ctrl+A  crouch + left      -> Select all
   *   Ctrl+R  crouch + reload    -> Reload the page, mid-round
   *   Ctrl+F  crouch + use       -> Find bar
   *   Ctrl+G  crouch + grenade   -> Find next
   *   Ctrl+E  crouch + use       -> Search from the address bar
   *   Ctrl+P  crouch + prone     -> Print dialog
   *
   * Any of those steals focus, which drops pointer lock, which pauses the
   * game — so a crouched retreat ends with a save dialog over the screen.
   *
   * The list is deliberately every letter the major browsers bind rather than
   * only the ones we can name a game action for: the cost of claiming a key
   * nothing uses is zero while the lock is held, and the cost of missing one
   * is a dialog in the middle of a firefight.
   */
  static MODIFIED_KEYS = new Set([
    'KeyA', 'KeyB', 'KeyD', 'KeyE', 'KeyF', 'KeyG', 'KeyH', 'KeyI', 'KeyJ',
    'KeyK', 'KeyL', 'KeyM', 'KeyN', 'KeyO', 'KeyP', 'KeyR', 'KeyS', 'KeyT',
    'KeyU', 'KeyW', 'KeyX', 'KeyY',
    // Zoom. Cmd/Ctrl with the plus, minus or zero key resizes the whole page,
    // which resizes the canvas and the HUD with it.
    'Minus', 'Equal', 'Digit0', 'NumpadAdd', 'NumpadSubtract',
  ]);

  /**
   * Function keys that open browser UI. F11 and F12 are deliberately NOT here:
   * fullscreen is something a player wants mid-game, and blocking devtools
   * makes the thing impossible to develop.
   */
  static FUNCTION_KEYS = new Set(['F1', 'F3', 'F4', 'F6', 'F7', 'F9', 'F10']);

  constructor(domElement) {
    this.dom = domElement;
    this.keys = new Set();
    this.pressedThisFrame = new Set();
    this.mouse = { dx: 0, dy: 0, left: false, right: false, leftEdge: false, rightEdge: false, wheel: 0 };
    /*
     * Aim is the right mouse button, and a trackpad cannot hold one down while
     * the same finger is still steering — so X aims as well. Both sources feed
     * the one `mouse.right` bit everything already reads, so nothing outside
     * this file knows X exists: the weapon, the HUD and the network command
     * are untouched.
     */
    this._rmb = false;
    this._aimKey = false;
    this.locked = false;
    this.sensitivity = 0.0022;

    addEventListener('keydown', (e) => {
      // preventDefault FIRST, before the repeat guard.
      //
      // This used to sit below `if (e.repeat) return`, so holding a key let
      // every repeat through to the browser. On Tab that is very visible: the
      // first repeat moves focus off the canvas — which drops pointer lock and
      // looks exactly like Escape was pressed — and the next one lands in the
      // address bar. Holding Tab is the normal way to read a scoreboard, so it
      // happened constantly.
      //
      // The `locked` gate made it worse rather than better: the moment focus
      // was lost the lock went with it, so from then on nothing was prevented
      // at all and every further Tab walked further into the browser chrome.
      // Keys the game owns are now claimed whether or not the lock is held.
      if (Input.GAME_KEYS.has(e.code) && !isTextEntry(e.target)) e.preventDefault();
      // Everything else is conditional on the pointer lock, which IS the
      // play/pause state (`main.js` sets `paused = !locked`). While the lock
      // is held the browser's own shortcuts are only ever in the way; the
      // moment Escape releases it they all work again, so the menu, the
      // address bar, reload and devtools are one keypress away.
      else if (this.locked && !isTextEntry(e.target) && Input.isBrowserKey(e)) {
        e.preventDefault();
      }

      if (e.repeat) return;
      this.keys.add(e.code);
      this.pressedThisFrame.add(e.code);
      if (e.code === 'KeyX') { this._aimKey = true; this.mouse.rightEdge = true; this._syncAim(); }
    });
    addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
      if (e.code === 'KeyX') { this._aimKey = false; this._syncAim(); }
    });
    addEventListener('blur', () => {
      this.keys.clear();
      this.mouse.left = false;
      this._rmb = this._aimKey = false;
      this._syncAim();
    });

    this.dom.addEventListener('mousedown', (e) => {
      if (!this.locked) { this.requestLock(); return; }
      if (e.button === 0) { this.mouse.left = true; this.mouse.leftEdge = true; }
      if (e.button === 2) { this._rmb = true; this.mouse.rightEdge = true; this._syncAim(); }
    });
    addEventListener('mouseup', (e) => {
      if (e.button === 0) this.mouse.left = false;
      if (e.button === 2) { this._rmb = false; this._syncAim(); }
    });
    this.dom.addEventListener('contextmenu', (e) => e.preventDefault());
    addEventListener('wheel', (e) => { this.mouse.wheel += Math.sign(e.deltaY); }, { passive: true });

    addEventListener('mousemove', (e) => {
      if (!this.locked) return;
      this.mouse.dx += e.movementX * this.sensitivity;
      this.mouse.dy += e.movementY * this.sensitivity;
    });

    // Entering fullscreen mid-match is the moment Keyboard Lock becomes
    // available, and nothing else fires then.
    document.addEventListener('fullscreenchange', () => {
      if (this.locked && document.fullscreenElement) this._lockKeyboard();
      else if (!document.fullscreenElement) navigator.keyboard?.unlock?.();
    });

    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.dom;
      // Keyboard Lock has to follow the pointer lock in BOTH directions, or a
      // player who alt-tabs out of a fullscreen match finds Ctrl+W and Ctrl+T
      // still dead in a page that is no longer a game.
      if (this.locked) this._lockKeyboard();
      else navigator.keyboard?.unlock?.();
      this.onLockChange?.(this.locked);
    });
  }

  /**
   * Does this event belong to the browser rather than the game?
   *
   * Note what is NOT claimed: Escape (the pause menu, and the browser will not
   * give it up anyway), F11, F12, and any bare key — a bare letter is always
   * gameplay and preventing it would break nothing but cost a branch.
   */
  static isBrowserKey(e) {
    if (e.code === 'F5') return true;
    if (Input.FUNCTION_KEYS.has(e.code)) return true;
    if ((e.ctrlKey || e.metaKey) && Input.MODIFIED_KEYS.has(e.code)) return true;
    // Alt alone opens the menu bar on Windows and Alt+D jumps to the address
    // bar in Firefox. Alt+Tab and Alt+F4 belong to the window manager and
    // never reach us, so there is nothing to claim there.
    if (e.altKey && /^Key[A-Z]$/.test(e.code)) return true;
    return false;
  }

  requestLock() {
    this.dom.requestPointerLock?.();
    this._lockKeyboard();
  }

  /**
   * Keyboard Lock, for the shortcuts `preventDefault` cannot touch.
   *
   * Ctrl+W, Ctrl+T and Ctrl+N are RESERVED: Chrome delivers the keydown but
   * ignores `preventDefault`, so crouch-forward (Ctrl+W) closes the tab
   * outright. The Keyboard Lock API is the only way to claim them, and it only
   * works while the document is fullscreen — so this is a no-op until the
   * player presses F11, and it fails silently on browsers without it.
   *
   * Escape is excluded from the list on purpose. Locking it would mean the
   * pause menu needs a long press to open, and Escape is the way out of both
   * fullscreen and pointer lock; that is not a trade worth making to save a
   * tab-close.
   */
  _lockKeyboard() {
    const kb = navigator.keyboard;
    if (!kb?.lock || !document.fullscreenElement) return;
    kb.lock([
      'KeyW', 'KeyT', 'KeyN', 'Tab',
      'Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5',
      'Digit6', 'Digit7', 'Digit8', 'Digit9',
    ]).catch(() => { /* not fullscreen, or unsupported */ });
  }

  /** Aim is held if EITHER the right button or X is down. */
  _syncAim() { this.mouse.right = this._rmb || this._aimKey; }

  down(code) { return this.keys.has(code); }
  pressed(code) { return this.pressedThisFrame.has(code); }

  /** Call once at the very end of every frame. */
  endFrame() {
    this.pressedThisFrame.clear();
    this.mouse.dx = 0;
    this.mouse.dy = 0;
    this.mouse.leftEdge = false;
    this.mouse.rightEdge = false;
    this.mouse.wheel = 0;
  }
}
